// src/components/DriverProfileCard.jsx
import React, { useState } from 'react';
import { useDriverAuth } from '../hooks/useDriverAuth';
import { DRIVER_STATUS, VERIFICATION_STATUS } from '../store/driverSlice';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button/Button';

export function DriverProfileCard({ showActions = false, compact = false }) {
  const {
    driverRecord,
    getDriverStatusMessage,
    getVerificationStatusMessage
  } = useDriverAuth();

  const [showFullProfile, setShowFullProfile] = useState(false);

  if (!driverRecord) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="text-center">
          <p className="text-gray-600">Driver profile not available</p>
        </div>
      </div>
    );
  }

  // Use driverRecord for admin display
  const driver = driverRecord;

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case DRIVER_STATUS.ACTIVE:
      case DRIVER_STATUS.APPROVED:
        return 'success';
      case DRIVER_STATUS.PENDING:
        return 'warning';
      case DRIVER_STATUS.SUSPENDED:
      case DRIVER_STATUS.REJECTED:
        return 'danger';
      case DRIVER_STATUS.INACTIVE:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getVerificationBadgeVariant = (status) => {
    switch (status) {
      case VERIFICATION_STATUS.APPROVED:
        return 'success';
      case VERIFICATION_STATUS.UNDER_REVIEW:
      case VERIFICATION_STATUS.PENDING:
        return 'warning';
      case VERIFICATION_STATUS.DOCUMENTS_REQUIRED:
        return 'info';
      case VERIFICATION_STATUS.REJECTED:
        return 'danger';
      default:
        return 'default';
    }
  };

  if (compact) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-medium">
              {driver.personalInfo.firstName[0]}{driver.personalInfo.lastName[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-gray-900 truncate">
              {driver.personalInfo.firstName} {driver.personalInfo.lastName}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <Badge variant={getStatusBadgeVariant(driver.status)}>
                {driver.status.replace('_', ' ').toUpperCase()}
              </Badge>
              {driver.rating > 0 && (
                <div className="flex items-center space-x-1">
                  <span className="text-yellow-400">⭐</span>
                  <span className="text-sm text-gray-600">{driver.rating.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
          {showActions && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFullProfile(true)}
            >
              View Profile
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold text-xl">
                {driver.personalInfo.firstName[0]}{driver.personalInfo.lastName[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {driver.personalInfo.firstName} {driver.personalInfo.lastName}
              </h2>
              <p className="text-gray-600">{driver.personalInfo.email}</p>
              <p className="text-gray-600">{driver.personalInfo.phone}</p>
            </div>
          </div>

          <div className="text-right">
            <div className="space-y-2">
              <Badge variant={getStatusBadgeVariant(driver.status)}>
                {driver.status.replace('_', ' ').toUpperCase()}
              </Badge>
              <Badge variant={getVerificationBadgeVariant(driver.verificationStatus)}>
                {driver.verificationStatus.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        <div className="mb-6 space-y-2">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">{getDriverStatusMessage()}</p>
          </div>
          {getVerificationStatusMessage() && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">{getVerificationStatusMessage()}</p>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{driver.totalTrips || 0}</div>
            <div className="text-sm text-gray-600">Total Trips</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {driver.rating ? driver.rating.toFixed(1) : 'N/A'}
            </div>
            <div className="text-sm text-gray-600">Rating</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              ${(driver.totalEarnings || 0).toFixed(0)}
            </div>
            <div className="text-sm text-gray-600">Total Earnings</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {driver.lastActive ? new Date(driver.lastActive).toLocaleDateString() : 'N/A'}
            </div>
            <div className="text-sm text-gray-600">Last Active</div>
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Vehicle</label>
              <p className="mt-1 text-sm text-gray-900">
                {driver.vehicleInfo.year} {driver.vehicleInfo.make} {driver.vehicleInfo.model}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Color</label>
              <p className="mt-1 text-sm text-gray-900">{driver.vehicleInfo.color}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">License Plate</label>
              <p className="mt-1 text-sm text-gray-900 font-mono">{driver.vehicleInfo.plateNumber}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Vehicle Type</label>
              <p className="mt-1 text-sm text-gray-900 capitalize">{driver.vehicleInfo.vehicleType}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="border-t pt-6 mt-6">
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => setShowFullProfile(true)}
              >
                View Details
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Full Profile Modal */}
      {showFullProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Driver Profile</h2>
                <button
                  onClick={() => setShowFullProfile(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <DriverProfileCard showActions={false} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
